import { useEffect, useId, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const DELAY = 350;
const WIDTH = 320;

interface Spot {
  left: number;
  top: number;
  /** über dem Element (sonst darunter) */
  above: boolean;
}

/**
 * Erklärtext als Tooltip statt als Zeile unter der Überschrift: erscheint nach kurzem Verweilen mit der Maus auf dem Element
 * (Überschrift samt Eingabefeld), oberhalb davon bzw. darunter, wenn oben kein Platz ist. Für Screenreader steht der Text
 * zusätzlich unsichtbar im Element (`describedBy` für `aria-describedby`).
 */
export function useHintTip(text: string | null | undefined): { bind: Record<string, unknown>; tip: ReactNode; describedBy: string | undefined } {
  const id = useId();
  const [spot, setSpot] = useState<Spot | null>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  if (!text) return { bind: {}, tip: null, describedBy: undefined };

  const hide = (): void => {
    window.clearTimeout(timer.current);
    setSpot(null);
  };
  const bind = {
    'data-hint': '',
    onMouseEnter: (e: MouseEvent<HTMLElement>) => {
      const el = e.currentTarget;
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        const r = el.getBoundingClientRect();
        const above = r.top > 140;
        setSpot({ left: Math.max(8, Math.min(r.left, window.innerWidth - WIDTH - 8)), top: above ? r.top - 6 : r.bottom + 6, above });
      }, DELAY);
    },
    onMouseLeave: hide,
    // beim Schreiben stört der Tooltip nur
    onKeyDown: hide,
  };
  const tip = (
    <>
      <span id={`${id}-hint`} hidden>
        {text}
      </span>
      {spot &&
        createPortal(
          <div role="tooltip" className={`tooltip hint-tip${spot.above ? ' above' : ''}`} style={{ left: spot.left, top: spot.top }} data-testid="hint-tip">
            {text}
          </div>,
          document.body,
        )}
    </>
  );
  return { bind, tip, describedBy: `${id}-hint` };
}

/** Abschnittsüberschrift, deren Erklärtext als Tooltip auf ihr liegt (statt als Zeile darunter); `as="h4"` für Unterabschnitte. */
export function HintHeading({ hint, children, testId, as: Tag = 'h3' }: { hint: string; children: ReactNode; testId?: string; as?: 'h3' | 'h4' }) {
  const h = useHintTip(hint);
  return (
    <Tag {...h.bind} aria-describedby={h.describedBy} data-testid={testId}>
      {children}
      {h.tip}
    </Tag>
  );
}
